import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSave, FaSpinner } from 'react-icons/fa';
import { TbSettings } from 'react-icons/tb';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * 설정 페이지 컴포넌트
 * - language: 언어 설정 (ko/en)
 * - 업로드 가능한 PPT 크기, 개수 표시 (수정 불가)
 */
const SettingsView = ({
  userId,
  userEmail = '',
  showAlertModal,
  isMobile = false,
  // 읽기 전용 (수정 불가)
  maxUploadSizeMb = 100,
  maxPptCount = 10,
  // 수정 가능한 설정 (props에서 초기값)
  language: initialLanguage = 'ko',
  onSettingsChange
}) => {
  const { t, i18n } = useTranslation();

  // 설정 상태 (props에서 초기값)
  const [language, setLanguage] = useState(initialLanguage);

  // 저장 상태
  const [saving, setSaving] = useState(false);

  // 원본 데이터 (변경사항 감지용)
  const [originalSettings, setOriginalSettings] = useState({
    language: initialLanguage
  });

  // props가 변경되면 상태 업데이트
  useEffect(() => {
    setLanguage(initialLanguage);
    setOriginalSettings({
      language: initialLanguage
    });
  }, [initialLanguage]);

  // 변경사항 감지
  const hasUnsavedChanges = useCallback(() => {
    return language !== originalSettings.language;
  }, [language, originalSettings]);

  // 언어 변경 핸들러 (즉시 미리보기 적용)
  const handleLanguageChange = (newLang) => {
    setLanguage(newLang);
    i18n.changeLanguage(newLang); // 즉시 UI에 반영
  };

  // 설정 저장
  const handleSave = async () => {
    if (!userId) return;

    setSaving(true);
    try {
      const userDocRef = doc(db, 'USERS', userId);
      await updateDoc(userDocRef, {
        'settings.language': language
      });

      setOriginalSettings({ language });

      // 부모 컴포넌트에 설정 변경 알림
      if (onSettingsChange) {
        onSettingsChange({ language });
      }

      showAlertModal(t('settings.saveSuccess'), 'success');
    } catch (err) {
      console.error('설정 저장 오류:', err);
      showAlertModal(t('settings.saveError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  // 스타일
  const styles = {
    container: {
      flex: 1,
      padding: isMobile ? '20px 16px' : '32px 48px',
      backgroundColor: '#f8fafc',
      overflowY: 'auto',
      minHeight: 'calc(100vh - 120px)'
    },
    header: {
      marginBottom: '32px'
    },
    title: {
      fontSize: isMobile ? '24px' : '28px',
      fontWeight: '700',
      color: '#1f2937',
      margin: '0 0 8px 0',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    subtitle: {
      fontSize: '14px',
      color: '#6b7280',
      margin: 0
    },
    section: {
      backgroundColor: '#ffffff',
      borderRadius: '16px',
      padding: isMobile ? '20px' : '28px',
      marginBottom: '24px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
    },
    sectionTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#1f2937',
      margin: '0 0 20px 0',
      paddingBottom: '12px',
      borderBottom: '1px solid #e5e7eb'
    },
    settingRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '16px 0',
      borderBottom: '1px solid #f3f4f6'
    },
    settingRowLast: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '16px 0'
    },
    settingLabel: {
      flex: 1
    },
    settingName: {
      fontSize: '15px',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '4px'
    },
    settingDescription: {
      fontSize: '13px',
      color: '#9ca3af'
    },
    settingValue: {
      fontSize: '15px',
      fontWeight: '600',
      color: '#1A62EA',
      backgroundColor: '#EDF4FE',
      padding: '8px 16px',
      borderRadius: '8px'
    },
    emailValue: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#374151',
      backgroundColor: '#f3f4f6',
      padding: '8px 16px',
      borderRadius: '8px',
      maxWidth: isMobile ? '180px' : 'none',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    radioGroup: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? '10px' : '16px',
      flexShrink: 0
    },
    radioLabel: (isSelected) => ({
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 16px',
      borderRadius: '10px',
      border: `2px solid ${isSelected ? '#1A62EA' : '#e5e7eb'}`,
      backgroundColor: isSelected ? '#EDF4FE' : '#ffffff',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      fontSize: '14px',
      fontWeight: isSelected ? '600' : '500',
      color: isSelected ? '#1A62EA' : '#374151'
    }),
    radioInput: {
      display: 'none'
    },
    radioCircle: (isSelected) => ({
      width: '18px',
      height: '18px',
      borderRadius: '50%',
      border: `2px solid ${isSelected ? '#1A62EA' : '#d1d5db'}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#ffffff'
    }),
    radioInner: {
      width: '10px',
      height: '10px',
      borderRadius: '50%',
      backgroundColor: '#1A62EA'
    },
    saveButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      padding: '14px 32px',
      backgroundColor: hasUnsavedChanges() ? '#1A62EA' : '#9ca3af',
      color: '#ffffff',
      border: 'none',
      borderRadius: '12px',
      fontSize: '15px',
      fontWeight: '600',
      cursor: hasUnsavedChanges() ? 'pointer' : 'not-allowed',
      transition: 'all 0.2s ease',
      marginTop: '8px'
    },
    spinner: {
      animation: 'spin 1s linear infinite'
    }
  };

  return (
    <div style={styles.container}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>

      {/* 헤더 */}
      <div style={styles.header}>
        <h1 style={styles.title}>
          <TbSettings size={isMobile ? 28 : 32} color="#1A62EA" />
          {t('settings.title')}
        </h1>
        <p style={styles.subtitle}>{t('settings.subtitle')}</p>
      </div>

      {/* 계정 정보 섹션 */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>{t('settings.accountInfo.title')}</h2>

        <div style={styles.settingRow}>
          <div style={styles.settingLabel}>
            <div style={styles.settingName}>{t('settings.accountInfo.email')}</div>
            <div style={styles.settingDescription}>{t('settings.accountInfo.emailDesc')}</div>
          </div>
          <div style={styles.emailValue}>{userEmail || '-'}</div>
        </div>

        <div style={styles.settingRow}>
          <div style={styles.settingLabel}>
            <div style={styles.settingName}>{t('settings.accountInfo.maxUploadSize')}</div>
            <div style={styles.settingDescription}>{t('settings.accountInfo.maxUploadSizeDesc')}</div>
          </div>
          <div style={styles.settingValue}>{maxUploadSizeMb} MB</div>
        </div>

        <div style={styles.settingRowLast}>
          <div style={styles.settingLabel}>
            <div style={styles.settingName}>{t('settings.accountInfo.maxPptCount')}</div>
            <div style={styles.settingDescription}>{t('settings.accountInfo.maxPptCountDesc')}</div>
          </div>
          <div style={styles.settingValue}>{maxPptCount}{t('settings.accountInfo.unit')}</div>
        </div>
      </div>

      {/* 언어 설정 섹션 */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>{t('settings.language.title')}</h2>

        <div style={styles.settingRowLast}>
          <div style={styles.settingLabel}>
            <div style={styles.settingName}>{t('settings.language.appLanguage')}</div>
            <div style={styles.settingDescription}>{t('settings.language.appLanguageDesc')}</div>
          </div>
          <div style={styles.radioGroup}>
            <label
              style={styles.radioLabel(language === 'ko')}
              onClick={() => handleLanguageChange('ko')}
            >
              <div style={styles.radioCircle(language === 'ko')}>
                {language === 'ko' && <div style={styles.radioInner} />}
              </div>
              {t('settings.language.korean')}
            </label>
            <label
              style={styles.radioLabel(language === 'en')}
              onClick={() => handleLanguageChange('en')}
            >
              <div style={styles.radioCircle(language === 'en')}>
                {language === 'en' && <div style={styles.radioInner} />}
              </div>
              {t('settings.language.english')}
            </label>
          </div>
        </div>
      </div>

      {/* 저장 버튼 */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          style={styles.saveButton}
          onClick={handleSave}
          disabled={!hasUnsavedChanges() || saving}
        >
        {saving ? (
          <>
            <FaSpinner size={16} style={styles.spinner} />
            {t('settings.saving')}
          </>
        ) : (
          <>
            <FaSave size={16} />
            {t('settings.saveButton')}
          </>
        )}
        </button>
      </div>
    </div>
  );
};

export default SettingsView;
